/**
 * Theme Context Provider
 * Manages the current theme + variant and provides switching functionality.
 *
 * Storage format: `<theme>-<variant>` (e.g., `ocean-dark`, `default-medium`).
 * Legacy stored value `"dark"` is migrated to `{ theme: 'default', variant: 'dark' }` on read.
 */

import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { getTheme, hasTheme, type ThemeName } from '../colors/themes';
import { ThemeVariant } from '../colors/themes/identifiers';
import { applyVariant } from '../colors/themes/variant-transform';
import { ColorPalette } from '../colors/types';
import { getInitialPreferences } from '@/hooks/usePreferenceManager';
import { usePreferencesQuery } from '@/hooks/queries';
import { logger } from '@/utils/logger';

// eslint-disable-next-line react-refresh/only-export-components -- re-export for consumer convenience
export { ThemeVariant };

interface ThemeContextValue {
  theme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  resetTheme: () => void;
  variant: ThemeVariant;
  setVariant: (variant: ThemeVariant) => void;
}

// eslint-disable-next-line react-refresh/only-export-components -- exported context object alongside provider
export const ThemeContext = createContext<ThemeContextValue | undefined>(
  undefined
);

// ---------------------------------------------------------------------------
// Storage helpers (exported for consumers like AppProfilePopup)
// ---------------------------------------------------------------------------

/**
 * Parse a stored preference string into { theme, variant }.
 *
 * | Stored value    | Result                              |
 * |-----------------|-------------------------------------|
 * | `"dark"`        | `{ theme:'default', variant:'dark'}`|
 * | `"ocean"`       | `{ theme:'ocean',   variant:'light'}`|
 * | `"ocean-medium"`| `{ theme:'ocean',   variant:'medium'}`|
 * | `"forest-dark"` | `{ theme:'forest',  variant:'dark'}`|
 */
// eslint-disable-next-line react-refresh/only-export-components -- utility function co-located with provider
export function parseStoredTheme(stored: string): {
  theme: ThemeName;
  variant: ThemeVariant;
} {
  // Legacy standalone dark theme → default-dark
  if (stored === 'dark') {
    return { theme: 'default' as ThemeName, variant: ThemeVariant.DARK };
  }

  // Try suffix match for variant
  for (const v of [
    ThemeVariant.DARK,
    ThemeVariant.MEDIUM,
    ThemeVariant.LIGHT,
  ]) {
    if (stored.endsWith(`-${v}`)) {
      const base = stored.slice(0, -(v.length + 1));
      if (hasTheme(base)) {
        return { theme: base as ThemeName, variant: v };
      }
    }
  }

  // Plain theme name (no suffix) → light variant
  if (hasTheme(stored)) {
    return { theme: stored as ThemeName, variant: ThemeVariant.LIGHT };
  }

  // Fallback
  return { theme: 'default' as ThemeName, variant: ThemeVariant.LIGHT };
}

/**
 * Encode a theme + variant pair for persistent storage.
 * Always emits the suffix so the format is unambiguous.
 */
// eslint-disable-next-line react-refresh/only-export-components -- utility function co-located with provider
export function encodeThemeVariant(
  theme: ThemeName,
  variant: ThemeVariant
): string {
  return `${theme}-${variant}`;
}

// ---------------------------------------------------------------------------
// CSS variable sync
// ---------------------------------------------------------------------------

/**
 * Convert hex color to HSL format string for CSS custom properties
 */
function hexToHSL(hex: string): string {
  hex = hex.replace('#', '');

  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Sync CSS custom properties with the variant-applied palette.
 * Receives the already-transformed palette (not the base theme name).
 */
function syncCSSVariables(palette: ColorPalette): void {
  const root = document.documentElement;

  root.style.setProperty('--color-primary', hexToHSL(palette.primary));
  root.style.setProperty('--color-success', hexToHSL(palette.success));
  root.style.setProperty('--color-purple', hexToHSL(palette.purple));
  root.style.setProperty('--color-blue', hexToHSL(palette.blue));
  root.style.setProperty('--color-error', hexToHSL(palette.error));
  root.style.setProperty('--color-rust', hexToHSL(palette.rust));
  root.style.setProperty('--color-teal', hexToHSL(palette.teal));
  root.style.setProperty('--color-light-purple', hexToHSL(palette.lightPurple));
  root.style.setProperty('--color-forest', hexToHSL(palette.forest));
  root.style.setProperty('--color-coral', hexToHSL(palette.coral));

  root.style.setProperty('--color-dark', hexToHSL(palette.dark));
  root.style.setProperty('--color-medium', hexToHSL(palette.medium));
  root.style.setProperty('--color-light', hexToHSL(palette.light));
  root.style.setProperty('--color-background', hexToHSL(palette.background));
  root.style.setProperty('--color-off-white', hexToHSL(palette.offWhite));

  // Shadcn/ui semantic variables — set directly from palette so they stay
  // in sync with the algorithmic variant (light/medium/dark) instead of
  // relying on the static `.dark` CSS class which can't track palette shifts.
  root.style.setProperty('--background', hexToHSL(palette.background));
  root.style.setProperty('--foreground', hexToHSL(palette.text.primary));
  root.style.setProperty('--card', hexToHSL(palette.offWhite));
  root.style.setProperty('--card-foreground', hexToHSL(palette.text.primary));
  root.style.setProperty('--popover', hexToHSL(palette.offWhite));
  root.style.setProperty(
    '--popover-foreground',
    hexToHSL(palette.text.primary)
  );
  root.style.setProperty('--primary', hexToHSL(palette.primary));
  root.style.setProperty(
    '--primary-foreground',
    hexToHSL(palette.text.inverse)
  );
  root.style.setProperty('--secondary', hexToHSL(palette.offWhite));
  root.style.setProperty(
    '--secondary-foreground',
    hexToHSL(palette.text.primary)
  );
  root.style.setProperty('--muted', hexToHSL(palette.offWhite));
  root.style.setProperty(
    '--muted-foreground',
    hexToHSL(palette.text.secondary)
  );
  root.style.setProperty('--accent', hexToHSL(palette.hover.background));
  root.style.setProperty('--accent-foreground', hexToHSL(palette.hover.text));
  root.style.setProperty('--destructive', hexToHSL(palette.error));
  root.style.setProperty(
    '--destructive-foreground',
    hexToHSL(palette.text.inverse)
  );
  root.style.setProperty('--border', hexToHSL(palette.border.default));
  root.style.setProperty('--input', hexToHSL(palette.border.light));
  root.style.setProperty('--ring', hexToHSL(palette.primary));
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: ThemeName;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'default' as ThemeName,
}) => {
  const [theme, setThemeState] = useState<ThemeName>(() => {
    const storedTheme = getInitialPreferences()?.theme;
    if (storedTheme) {
      return parseStoredTheme(storedTheme).theme;
    }
    return defaultTheme;
  });

  const [variant, setVariantState] = useState<ThemeVariant>(() => {
    const storedTheme = getInitialPreferences()?.theme;
    if (storedTheme) {
      return parseStoredTheme(storedTheme).variant;
    }
    return ThemeVariant.LIGHT;
  });

  useEffect(() => {
    const basePalette = getTheme(theme).palette;
    const palette = applyVariant(basePalette, variant);

    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    root.setAttribute('data-variant', variant);

    syncCSSVariables(palette);

    // Toggle Tailwind dark class based on variant (not theme name)
    if (variant === ThemeVariant.DARK) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme, variant]);

  // Subscribe to preference changes via React Query cache
  const { data: queryPreferences } = usePreferencesQuery();

  useEffect(() => {
    if (queryPreferences?.theme) {
      const { theme: newTheme, variant: newVariant } = parseStoredTheme(
        queryPreferences.theme
      );
      logger.ui.debug('[ThemeContext] Updating theme from query cache:', {
        theme: newTheme,
        variant: newVariant,
      });
      setThemeState(newTheme);
      setVariantState(newVariant);
    }
  }, [queryPreferences]);

  const setTheme = (newTheme: ThemeName) => {
    setThemeState(newTheme);
  };

  const setVariant = (newVariant: ThemeVariant) => {
    setVariantState(newVariant);
  };

  const resetTheme = () => {
    setThemeState(defaultTheme);
    setVariantState(ThemeVariant.LIGHT);
  };

  return (
    <ThemeContext.Provider
      value={{ theme, setTheme, resetTheme, variant, setVariant }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
